import type { AiProfile } from "@/types/ai";
import { VoiceTranscriptBuffer } from "./voiceTranscriptBuffer";
import { judgeVoiceIntent } from "./voiceIntentJudge";
import { VoiceEchoDetector } from "./voiceEchoDetector";
import { getUserId } from "@/lib/user/identity";

export interface VoicePilotAction {
  type: string;
  [key: string]: any;
}

export interface VoicePilotContext {
  activeTiles: Array<{
    id: string;
    name: string;
    type: string;
    profileName?: string;
    minimized?: boolean;
  }>;
  availableProfiles: Array<{ id: string; name: string }>;
  awareness: string;
  status: string;
  recentCommands: string[];
}

export interface VoicePilotResult {
  message: string;
  audio?: { url: string; provider: string };
  actions: VoicePilotAction[];
  needsConfirmation: boolean;
  needsPlanning?: boolean;
  think?: string;
  provider: string;
  model: string;
  directed: boolean;
  stop: boolean;
}

export async function sendVoicePilotMessage(
  message: string,
  profile: AiProfile,
  context: VoicePilotContext,
  conversationHistory: Array<{ role: "user" | "assistant"; content: string }> = [],
  tts: "browser" | "remote" = "remote"
): Promise<VoicePilotResult> {
  const res = await fetch("/api/karma-ac/voice/pilot", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      profile,
      message,
      workspaceContext: context,
      conversationHistory,
      tts,
      userId: getUserId(),
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Voice pilot request failed");
  }

  const data = await res.json();
  return {
    message: data.message ?? "",
    audio: data.audio,
    actions: Array.isArray(data.actions) ? data.actions : [],
    needsConfirmation: data.needsConfirmation ?? false,
    needsPlanning: data.needsPlanning ?? false,
    think: data.think,
    provider: data.provider,
    model: data.model,
    directed: true,
    stop: false,
  };
}

export function estimateTtsDuration(text: string, wpm = 200): number {
  const words = text.trim().split(/\s+/).length;
  if (wpm <= 0) return 0;
  return (words / wpm) * 60 + 0.5;
}

export class VoicePilotSession {
  private buffer: VoiceTranscriptBuffer;
  private conversationHistory: Array<{ role: "user" | "assistant"; content: string }> = [];
  private assistantName: string;
  private hotWindow = false;
  private hotWindowExpiresAt = 0;
  private ttsPlayingUntil = 0;
  private echoDetector: VoiceEchoDetector;
  private readonly hotWindowDurationSec = 45;

  constructor(assistantName = "Karma") {
    this.assistantName = assistantName;
    this.buffer = new VoiceTranscriptBuffer(120);
    this.echoDetector = new VoiceEchoDetector();
  }

  private isHotWindowActive(): boolean {
    if (!this.hotWindow) return false;
    if (Date.now() / 1000 > this.hotWindowExpiresAt) {
      this.hotWindow = false;
      return false;
    }
    return true;
  }

  private extendHotWindow(): void {
    this.hotWindow = true;
    this.hotWindowExpiresAt = Date.now() / 1000 + this.hotWindowDurationSec;
  }

  setTtsPlaying(durationSec: number): void {
    this.ttsPlayingUntil = (Date.now() / 1000) + durationSec;
  }

  isTtsPlaying(): boolean {
    return Date.now() / 1000 < this.ttsPlayingUntil;
  }

  setAssistantName(name: string): void {
    this.assistantName = name;
  }

  /** Called when user manually triggers voice (e.g., clicks mic) so the next utterance is treated as directed. */
  warmUp(): void {
    this.extendHotWindow();
  }

  trackTtsFinish(): void {
    this.echoDetector.trackTtsFinish();
  }

  stop(): void {
    this.hotWindow = false;
    this.ttsPlayingUntil = 0;
  }

  async processTranscript(
    rawTranscript: string,
    profile: AiProfile,
    context: VoicePilotContext
  ): Promise<VoicePilotResult> {
    const isDuringTts = this.isTtsPlaying();

    // Check if the transcript is an echo of Karma's own TTS output
    const echoCheck = this.echoDetector.checkEcho(rawTranscript);
    if (echoCheck.isEcho) {
      this.buffer.add(rawTranscript, true);
      return {
        message: "",
        actions: [],
        needsConfirmation: false,
        provider: "echo-detector",
        model: "echo-detector",
        directed: false,
        stop: false,
      };
    }

    this.buffer.add(rawTranscript, isDuringTts);

    // Extend hot window if user has been engaged recently or TTS just finished
    if (!isDuringTts) {
      this.hotWindow = true;
    }

    // Stop commands work even inside the hot window.
    const stopPhrases = ["stop listening", "stop", "shut up", "be quiet", "quiet", "go away", "end voice", "cancel voice"];
    const lower = rawTranscript.toLowerCase().trim();
    if (stopPhrases.some((p) => lower === p || lower.startsWith(p + " "))) {
      this.stop();
      return {
        message: "",
        actions: [],
        needsConfirmation: false,
        provider: "hot-window",
        model: "hot-window",
        directed: true,
        stop: true,
      };
    }

    // Hot window = user already engaged; skip the intent judge to cut latency in half.
    let query = rawTranscript;
    if (!this.isHotWindowActive()) {
      const judgment = await judgeVoiceIntent(this.assistantName, rawTranscript, this.buffer, this.hotWindow);

      if (judgment.stop) {
        this.hotWindow = false;
        return {
          message: "",
          actions: [],
          needsConfirmation: false,
          provider: "intent-judge",
          model: "intent-judge",
          directed: true,
          stop: true,
        };
      }

      if (!judgment.directed) {
        return {
          message: "",
          actions: [],
          needsConfirmation: false,
          provider: "intent-judge",
          model: "intent-judge",
          directed: false,
          stop: false,
        };
      }

      query = judgment.query;
    }

    const result = await sendVoicePilotMessage(query, profile, context, this.conversationHistory);

    // Add to conversation history for context across turns
    this.conversationHistory.push({ role: "user", content: query });
    this.conversationHistory.push({ role: "assistant", content: result.message });
    if (this.conversationHistory.length > 20) {
      this.conversationHistory = this.conversationHistory.slice(-20);
    }

    // Mark user segments as processed so they don't get re-judged
    for (const segment of this.buffer.getUnprocessed()) {
      if (segment.text === rawTranscript) {
        this.buffer.markProcessed(segment);
      }
    }

    // Estimate TTS duration so the next transcript can be marked as echo if it occurs during playback
    if (result.message) {
      const duration = estimateTtsDuration(result.message);
      this.setTtsPlaying(duration);
      this.echoDetector.trackTtsStart(result.message, duration);
    }

    return { ...result, directed: true, stop: false };
  }

  getRecentTranscripts(): string[] {
    return this.buffer.getLastSeconds(120).map((s) => s.text);
  }
}
